import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwise, ArrowSquareOut, CheckCircle, Warning, CaretDown, CaretUp } from "@phosphor-icons/react";
import { formatDate, cn } from "@/lib/utils";

const PAGE = 8;

/**
 * Admin-only "Suspected mis-merges" panel. Lists thread messages that the
 * dedup likely swallowed into the wrong lead (different subject + different
 * external sender). Each row can be split off as a fresh lead (treated like
 * a new enquiry) or confirmed as legitimately belonging to its lead.
 */
export default function MismergeAuditTile({ isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/leads/audit/mismerges");
      setRows(data.suspects || []);
    } catch (e) {
      console.error("Mis-merge audit load failed:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!isAdmin) return null;

  const keyOf = (r) => `${r.lead_id}:${r.message_index}`;

  const split = async (r) => {
    if (!window.confirm(`Split "${r.subject}" off "${r.lead_name || r.lead_subject}" into its own new lead? It will be routed and notified like a fresh enquiry.`)) return;
    setBusyKey(keyOf(r));
    try {
      const { data } = await api.post(`/leads/${r.lead_id}/split`, { message_indices: r.message_indices || [r.message_index], notify: true });
      toast.success(`New lead created: ${data.name || data.email_subject || "Untitled"}`, {
        action: { label: "Open", onClick: () => window.open(`/leads/${data.id}`, "_blank") },
        duration: 10000,
      });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split failed");
    } finally {
      setBusyKey(null);
    }
  };

  const dismiss = async (r) => {
    setBusyKey(keyOf(r));
    try {
      await api.post(`/leads/${r.lead_id}/mismerge-dismiss`, { message_indices: r.message_indices || [r.message_index] });
      setRows((prev) => prev.filter((x) => keyOf(x) !== keyOf(r)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    } finally {
      setBusyKey(null);
    }
  };

  const visible = showAll ? rows : rows.slice(0, PAGE);

  return (
    <div data-testid="mismerge-audit-tile" className="bg-white border border-slate-200 rounded-sm p-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="label-tracking">Data quality</div>
          <h3 className="font-display text-xl text-slate-900 mt-1">Suspected mis-merges</h3>
          <p className="text-xs text-slate-500 mt-1 max-w-xl leading-relaxed">
            Emails that landed inside another lead's thread but look like a separate matter (different subject and sender). Split them off so they get tracked and routed, or confirm they belong.
          </p>
        </div>
        <div className="inline-flex items-center gap-2">
          <span
            data-testid="mismerge-count-chip"
            className={cn(
              "inline-flex items-center gap-1.5 px-2.5 py-1 border rounded-sm text-xs font-medium",
              loading ? "bg-slate-100 text-slate-700 border-slate-200"
                : rows.length ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-emerald-50 text-emerald-800 border-emerald-200"
            )}
          >
            {loading ? <ArrowsClockwise size={14} className="animate-spin" /> : rows.length ? <Warning size={14} weight="fill" /> : <CheckCircle size={14} weight="fill" />}
            {loading ? "Scanning…" : rows.length ? `${rows.length} to review` : "All clear"}
          </span>
          <button data-testid="mismerge-collapse-btn" onClick={() => setCollapsed((c) => !c)} className="text-slate-400 hover:text-slate-900" title={collapsed ? "Expand" : "Collapse"}>
            {collapsed ? <CaretDown size={16} /> : <CaretUp size={16} />}
          </button>
        </div>
      </div>

      {!collapsed && !loading && rows.length > 0 && (
        <div className="mt-5 divide-y divide-slate-100 border-t border-slate-100">
          {visible.map((r) => {
            const k = keyOf(r);
            const busy = busyKey === k;
            return (
              <div key={k} data-testid={`mismerge-row-${r.lead_id}-${r.message_index}`} className="py-3 grid grid-cols-1 lg:grid-cols-[1fr_1fr_auto] gap-3 lg:gap-6 items-start">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Buried email</div>
                  <div className="text-sm text-slate-900 font-medium truncate" title={r.subject}>
                    {r.subject || "(no subject)"}
                    {(r.message_indices?.length || 1) > 1 && <span className="ml-2 text-[10px] font-normal text-slate-500">+{r.message_indices.length - 1} more in this sub-thread</span>}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.from_name || r.from_email} {r.from_email && <span className="font-mono">&lt;{r.from_email}&gt;</span>} · {formatDate(r.sent_at)}
                  </div>
                  {r.preview && <div className="text-xs text-slate-400 mt-1 line-clamp-1">{r.preview}</div>}
                </div>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-slate-400">Currently inside lead</div>
                  <Link to={`/leads/${r.lead_id}`} className="text-sm text-[#002FA7] hover:underline truncate block" title={r.lead_subject}>
                    {r.lead_name || "Unknown contact"}
                  </Link>
                  <div className="text-xs text-slate-500 truncate">{r.lead_subject}</div>
                </div>
                <div className="inline-flex items-center gap-2 lg:justify-end">
                  <button
                    data-testid={`mismerge-split-btn-${r.lead_id}-${r.message_index}`}
                    disabled={busyKey !== null}
                    onClick={() => split(r)}
                    className="text-xs inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40 transition-colors"
                  >
                    <ArrowSquareOut size={13} /> {busy ? "Splitting…" : "Split into new lead"}
                  </button>
                  <button
                    data-testid={`mismerge-dismiss-btn-${r.lead_id}-${r.message_index}`}
                    disabled={busyKey !== null}
                    onClick={() => dismiss(r)}
                    className="text-xs px-3 py-1.5 rounded-sm border border-slate-200 text-slate-600 hover:bg-slate-50 disabled:opacity-40 transition-colors"
                  >
                    Belongs here
                  </button>
                </div>
              </div>
            );
          })}
          {rows.length > PAGE && (
            <button
              data-testid="mismerge-show-all-btn"
              onClick={() => setShowAll((s) => !s)}
              className="pt-3 text-xs text-slate-500 hover:text-slate-900"
            >
              {showAll ? "Show fewer" : `Show all ${rows.length}`}
            </button>
          )}
        </div>
      )}
      {!collapsed && !loading && rows.length === 0 && (
        <p className="mt-4 text-xs text-slate-500">No suspicious thread messages found.</p>
      )}
    </div>
  );
}
